/* eslint-disable no-empty */
import { useCallback, useMemo, useState } from "react"

import { QueryClientProvider } from "@tanstack/react-query"

import createStore from "@Stores/base-factories";
import Cookies from "@Utils/cookies-helper";
import history from "@Utils/history"
import HttpClientLegacy, { internalApi as HttpClient } from '@Utils/httpclient'
import menu from '@Utils/menu'
import { queryClient } from "@Utils/queries";

const NOT_AUTHENTICATED = 'NOT_AUTHENTICATED'
const AUTHENTICATING = 'AUTHENTICATING'
const AUTHENTICATED = 'AUTHENTICATED'

const hookGlobalAuthFactory = () => ({ fetch }) => {
  const [currentState, setCurrentState] = useState(NOT_AUTHENTICATED)
  const [currentSession, setCurrentSession] = useState(() => {
    try {
      const session = JSON.parse(localStorage.getItem('session') || '{}')
      const cookieSessionId = Cookies.getSessionId()

      if (cookieSessionId || session.session_id) {
        setCurrentState(AUTHENTICATED)
      }

      if (cookieSessionId && (!session.session_id || session.session_id !== cookieSessionId)) {
        getCurrentSession({ headers: { 'X-API-Key': cookieSessionId } })
        return { session_id: cookieSessionId }
      }

      return session
    } catch {
      return null
    }
  })
  const [error, setError] = useState(null)

  function updateCookie(session) {
    Cookies.setSessionId(session)
  }

  function updateLocalStorage(session) {
    localStorage.setItem('session', JSON.stringify(session))
  }

  const clearSession = useCallback(() => {
    setCurrentSession(null)
    localStorage.removeItem('session');
    Cookies.clearSession();
    setCurrentState(NOT_AUTHENTICATED)
  }, [])

  const login = useCallback(async payload => {
    setCurrentState(AUTHENTICATING)

    try {
      payload.email = payload?.email.toLowerCase()
      const data = await fetch({
        method: 'post',
        url: '/login',
        data: payload,
      })

      if (data.twoFactorRequired) {
        setError(null)
        return data
      }

      HttpClient.defaults.headers['X-API-Key'] = data.session_id;
      HttpClientLegacy.defaults.headers['X-API-Key'] = data.session_id;

      setCurrentSession(data)
      setError(null)
      updateLocalStorage(data)
      updateCookie(data)
      setCurrentState(AUTHENTICATED)
    } catch (error) {
      clearSession()
      setError('Incorrect email or password.')
      throw error
    }
  }, [clearSession, fetch])

  const verifyLogin = useCallback(async payload => {
    setCurrentState(AUTHENTICATING)

    try {
      const data = await fetch({
        method: 'post',
        url: '/login/verify',
        data: payload,
      })

      HttpClient.defaults.headers['X-API-Key'] = data.session_id;
      HttpClientLegacy.defaults.headers['X-API-Key'] = data.session_id;

      setCurrentSession(data)
      setError(null)
      updateLocalStorage(data)
      updateCookie(data)
      setCurrentState(AUTHENTICATED)
    } catch (error) {
      clearSession()
      setError('Incorrect or expired code. Please try again!')
      throw error
    }
  }, [clearSession, fetch])

  const getCurrentSession = useCallback(async (options = {}) => {
    try {
      const data = await fetch({
        url: '/session/current',
        ...options,
      })

      setCurrentSession(data)
      updateLocalStorage(data)
      updateCookie(data)
      setError(null)
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        clearSession()
      }
      throw error
    }
  }, [fetch, clearSession])

  const logout = useCallback(async () => {
    try {
      await fetch({
        method: 'post',
        url: '/logout',
        data: {
          session_id: currentSession.session_id,
        },
      })
      clearSession()
      history.push('/auth/login');
    } catch { }
  }, [clearSession, currentSession, fetch])

  const verfifyCode = useCallback(validationCode => {
    const headers = { 'X-Validation-Code': validationCode };
    return fetch({
      url: '/forgot',
      headers,
    })
  }, [fetch])

  const resetPassword = useCallback(payload => fetch({
    url: '/forgot',
    method: 'put',
    data: payload,
  }), [fetch])

  const generateCode = useCallback(async payload => {
    payload.email = payload.email.toLowerCase()
    try {
      const { data } = await HttpClient.request({
        method: 'post',
        url: '/forgot',
        data: payload,
      })
      return data
    } catch (error) {
      const errorData = error?.response?.data
      if (errorData) return errorData
      throw error
    }
  }, [])

  const permissions = useMemo(() => {
    const hasSession = currentSession && currentSession.user
    const menuPermissions = !hasSession || !currentSession.organization ? [] : menu
      .filter(menuOption => currentSession.organization[menuOption.key])

    const isManagerAdmin = hasSession && currentSession.user.isManagerAdmin || false
    const isSuperAdmin = hasSession && currentSession.user.isSuperAdmin || false
    return {
      menuPermissions,
      isManagerAdmin,
      isSuperAdmin,
    }
  }, [currentSession])

  return useMemo(() => ({
    auth: {
      currentSession,
      getCurrentSession,
      clearSession,
      login,
      verifyLogin,
      logout,
      error,
      onChangeError: setError,
      isAuthenticated: currentState === AUTHENTICATED,
      ...permissions,
    },
    forgot: {
      verfifyCode,
      resetPassword,
      generateCode,
    },
  }), [currentSession, getCurrentSession, clearSession, login, verifyLogin, logout, error, currentState, permissions, verfifyCode, resetPassword, generateCode])
}

const { Provider, useStore } = createStore()
  .extend(hookGlobalAuthFactory())
  .factory()

function GlobalQueryProvider({ children }) {
  return <QueryClientProvider client={queryClient}>
    <Provider>
      {children}
    </Provider>
  </QueryClientProvider>
}

export {
  GlobalQueryProvider as GlobalAuthProvider,
  useStore as useGlobalAuthStore,
}
